// contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import WebAuthForm from '../components/WebAuthForm';
import { setGlobalAuthErrorHandler } from '../services/api';
import { removeCookie } from '../utils/cookies';
import { refreshAuthToken } from '../auth';
import globalSSEService from '../services/globalSSEService';

export const AuthContext = createContext();

const TOKEN_COOKIE_KEY = 'jwt_token';

export const AuthProvider = ({ children }) => {
    const { token: initialToken, error: initialError, updateToken, isTelegram } = useAuth();
    const [token, setToken] = useState(initialToken);
    const [error, setError] = useState(initialError);
    const [showAuthForm, setShowAuthForm] = useState(false);

    useEffect(() => {
        // Регистрируем глобальный обработчик ошибок авторизации
        setGlobalAuthErrorHandler((err) => {
            if (isTelegram) {
                window.alert('Authorization error. Please try again later or contact support.');
            } else {
                removeCookie(TOKEN_COOKIE_KEY);
                setToken(null);
                setShowAuthForm(true);
            }
        });

        // Register token refresher on globalSSEService so it can refresh expired tokens
        if (isTelegram) {
            const refreshFn = async () => {
                const result = await refreshAuthToken();
                if (result && result.token) {
                    setToken(result.token);
                    return result.token;
                }
                return null;
            };
            globalSSEService.setTokenRefresher(refreshFn);
        }

        if (!isTelegram && !initialToken && !initialError) {
            setShowAuthForm(true);
        } else {
            setShowAuthForm(false);
        }
        setToken(initialToken);
        setError(initialError);

        return () => {
            // Clean up refresher on unmount
            globalSSEService.setTokenRefresher(null);
        };
    }, [initialToken, initialError, isTelegram]);

    const handleAuth = (authData) => {
        updateToken(authData);
        setShowAuthForm(false);
        setError(null);
    };

    const handleCloseError = () => {
        setError(null);
        if (!isTelegram) {
            setShowAuthForm(true);
        }
        // В Telegram ничего не делаем!
    };

    return (
        <AuthContext.Provider value={token}>
            {error ? (
                <Dialog open={!!error} onClose={handleCloseError}>
                    <DialogTitle>{error.title || 'Authorization error'}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{error.description || 'Please try again later.'}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseError} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : showAuthForm ? (
                <WebAuthForm onAuth={handleAuth} />
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};